/**
 * Iatrode.UI - Avatar Component (用户头像组件)
 * 提供用户头像展示、下拉菜单交互
 */

(function($) {
    'use strict';

    // 默认配置
    const DEFAULTS = {
        src: null,              // 头像图片地址
        defaultIcon: '👤',      // 默认头像图标
        userName: '',           // 用户名
        userEmail: '',          // 用户邮箱
        status: null,           // 在线状态: 'online', 'offline', 'busy', 'away'
        showStatus: false,      // 是否显示状态指示器
        size: 'md',             // 尺寸: 'sm', 'md', 'lg', 'xl'
        hasNotification: false, // 是否有新通知
        menuItems: [],          // 菜单项配置
        autoClose: true,        // 点击菜单项后自动关闭
        onShow: null,           // 下拉菜单显示回调
        onHide: null,           // 下拉菜单隐藏回调
        onItemClick: null       // 菜单项点击回调
    };

    function isBoringAvatarElement($element) {
        const value = $element.data('boring-avatar');
        return value === true || value === 'true';
    }

    function normalizeAvatarSrc(rawSrc) {
        if (!rawSrc || typeof rawSrc !== 'string') {
            return null;
        }

        const src = rawSrc.trim();
        if (!src) {
            return null;
        }

        // 兼容外部注入的 SVG 字符串，将其安全转换为 data URI。
        if (src.startsWith('<svg') && src.endsWith('</svg>')) {
            return `data:image/svg+xml;utf8,${encodeURIComponent(src)}`;
        }

        return src;
    }

    function getDropdownCore() {
        return window.IatrodeUI && window.IatrodeUI.dropdown ? window.IatrodeUI.dropdown : null;
    }

    // Avatar 类
    class Avatar {
        constructor(element, options) {
            this.$element = $(element);
            this.options = $.extend({}, DEFAULTS, options);
            this.isOpen = false;
            this.instanceId = 'avatar-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
            this._resizeTimer = null;
            this.init();
        }

        init() {
            // 如果服务端模板里已经包含了一个下拉菜单（可能是嵌套在触发器内，
            // 也可能是作为同级/li 内部的兄弟节点），我们先把它摘离，避免 render() 覆盖掉它；
            // render() 完成后再把它放回并标记 instanceId。
            
            // 首先在 avatar 元素内部查找下拉菜单
            let $found = this.$element.find('.dropdown-menu').first();
            
            // 如果内部没找到，尝试在父容器中查找（可能是兄弟节点）
            if (!$found || $found.length === 0) {
                const $container = this.$element.closest('li, .nav-item');
                if ($container && $container.length) {
                    $found = $container.children('.dropdown-menu').first();
                    // 如果找到的元素其实是触发器内部的（极少数情况），忽略
                    if ($found.length && $found.closest(this.$element).length > 0) {
                        $found = $();
                    }
                }
            }
            
            if ($found && $found.length) {
                // 确保添加 ui-avatar-dropdown 类（如果还没有）
                $found.addClass('ui-avatar-dropdown');
                this._preservedDropdown = $found.detach();
            } else {
                this._preservedDropdown = $();
            }

            this.render();
            this.bindEvents();
        }

        render() {
            const { defaultIcon, userName, userEmail, status, showStatus, size, hasNotification, menuItems } = this.options;
            const src = normalizeAvatarSrc(this.options.src);

            // 构建头像 HTML
            let avatarHTML = '<div class="ui-avatar-img">';

            // 头像图片或默认图标
            if (src) {
                avatarHTML += `<img src="${src}" alt="${userName || 'User'}" onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">`;
                avatarHTML += `<div class="ui-avatar-default" style="display:none;"><i>${defaultIcon}</i></div>`;
            } else {
                avatarHTML += `<div class="ui-avatar-default"><i>${defaultIcon}</i></div>`;
            }

            // 状态指示器
            if (showStatus && status) {
                avatarHTML += `<span class="ui-avatar-status ${status}"></span>`;
            }

            avatarHTML += '</div>';

            // 下拉菜单（使用 Bootstrap 5 的命名：dropdown-menu / dropdown-item / dropdown-divider）
            if (menuItems && menuItems.length > 0) {
                // 标记 instanceId，方便当我们把菜单移动到 body 时仍能找到关联实例
                avatarHTML += `<div class="dropdown-menu ui-avatar-dropdown" data-avatar-instance="${this.instanceId}">`;

                // 菜单头部（用户信息） - 使用 bootstrap 的 dropdown-header 样式同时保留自定义类
                if (userName || userEmail) {
                    avatarHTML += '<div class="dropdown-header ui-avatar-dropdown-header">';
                    if (userName) {
                        avatarHTML += `<div class="user-name">${userName}</div>`;
                    }
                    if (userEmail) {
                        avatarHTML += `<div class="user-email">${userEmail}</div>`;
                    }
                    avatarHTML += '</div>';
                }

                // 菜单项列表（直接使用 .dropdown-item，divider 使用 .dropdown-divider）
                menuItems.forEach((item, index) => {
                    if (item.divider) {
                        avatarHTML += '<div class="dropdown-divider"></div>';
                    } else {
                        const dangerClass = item.danger ? ' danger' : '';
                        const icon = item.icon ? `<span class="icon">${item.icon}</span>` : '';
                        // 使用 button 方便绑定点击并保持无 href
                        avatarHTML += `<button type="button" class="dropdown-item ui-avatar-dropdown-item${dangerClass}" data-index="${index}"> ${icon}<span>${item.text}</span> </button>`;
                    }
                });

                avatarHTML += '</div>';
            }

            // 添加尺寸和通知类
            this.$element.addClass('ui-avatar');
            if (size !== 'md') {
                this.$element.addClass(`ui-avatar-${size}`);
            }
            if (hasNotification) {
                this.$element.addClass('has-notification');
            }

            this.$element.html(avatarHTML);

            // 如果之前有服务端渲染的下拉菜单，恢复它，并绑定 instanceId
            if (this._preservedDropdown && this._preservedDropdown.length) {
                // 确保菜单带有 instance 标识，以便事件委托和移动到 body 后的定位使用
                this._preservedDropdown.attr('data-avatar-instance', this.instanceId);
                this.$element.append(this._preservedDropdown);
                // 清理引用，避免重复使用
                this._preservedDropdown = null;
            } else {
                // 如果没有保存的下拉菜单，检查是否有新添加的（可能是在 render 之后由其他代码添加的）
                const $existingDropdown = this.$element.find('.dropdown-menu.ui-avatar-dropdown');
                if ($existingDropdown.length && !$existingDropdown.attr('data-avatar-instance')) {
                    $existingDropdown.attr('data-avatar-instance', this.instanceId);
                }
            }

            // 触发自定义事件，通知 boring avatar 集成脚本进行渲染
            this.$element.trigger('avatar.rendered');
        }

        bindEvents() {
            const self = this;

            // 点击头像切换下拉菜单
            this.$element.on('click.ui.avatar', '.ui-avatar-img', function(e) {
                e.stopPropagation();
                self.toggle();
            });

            // 点击菜单项（委托到 document，以便菜单可被移动到 body 时仍然响应）
            $(document).on('click.ui.avatar.item.' + this.instanceId, '.dropdown-menu.ui-avatar-dropdown .dropdown-item', function(e) {
                // 确保该菜单项属于当前实例（查找最近的 .dropdown-menu）
                const $dropdown = $(this).closest('.dropdown-menu.ui-avatar-dropdown');
                if (!$dropdown.length) return;
                if ($dropdown.data('avatar-instance') !== self.instanceId) return;

                e.stopPropagation();
                e.preventDefault();
                
                const index = $(this).data('index');
                const item = self.options.menuItems[index];

                if (item && typeof self.options.onItemClick === 'function') {
                    self.options.onItemClick.call(self.$element[0], item, index);
                }

                // 执行菜单项的回调
                if (item && typeof item.onClick === 'function') {
                    item.onClick.call(self.$element[0], item, index);
                }

                // 如果是链接（a 标签），让浏览器自然导航
                if ($(this).is('a') && $(this).attr('href')) {
                    window.location.href = $(this).attr('href');
                    return;
                }

                if (self.options.autoClose) {
                    self.hide();
                }
            });

            // 点击外部关闭下拉菜单
            $(document).on('click.ui.avatar.' + this.instanceId, function(e) {
                if (self.isOpen && !self.$element.is(e.target) && self.$element.has(e.target).length === 0) {
                    self.hide();
                }
            });

            // 窗口滚动时关闭下拉并恢复头像状态；调整大小时重新定位
            $(window).on('scroll.ui.avatar.' + this.instanceId, function() {
                if (self.isOpen) {
                    // 当用户滚动页面时，关闭下拉菜单并确保头像恢复默认（移除 active）
                    self.hide();
                }
            });

            $(window).on('resize.ui.avatar.' + this.instanceId + ' orientationchange.ui.avatar.' + this.instanceId, function() {
                if (!self.isOpen) return;
                window.clearTimeout(self._resizeTimer);
                self._resizeTimer = window.setTimeout(function() {
                    self.positionDropdown();
                }, 120);
            });
        }

        toggle() {
            if (this.isOpen) {
                this.hide();
            } else {
                this.show();
            }
        }

        show() {
            if (this.isOpen) return;

            // 关闭其他打开的头像下拉菜单
            $('.ui-avatar.active').not(this.$element).each(function() {
                const instance = $(this).data('ui.avatar.instance');
                if (instance) {
                    instance.hide();
                }
            });

            this.$element.addClass('active');
            this.isOpen = true;

            // 使用 requestAnimationFrame 确保 DOM 更新后再计算位置
            const self = this;
            requestAnimationFrame(function() {
                // 在显示前，将下拉菜单移动到 body（避免受父元素 transform/positioning 等影响）
                const $dropdown = self.$element.find('.dropdown-menu.ui-avatar-dropdown');
                if ($dropdown.length) {
                    // 把元素移动到 body，并保留 data-avatar-instance 标识
                    $dropdown.appendTo(document.body);
                }

                self.positionDropdown();
                const core = getDropdownCore();
                const moved = $(`[data-avatar-instance="${self.instanceId}"]`)[0];
                if (core && moved) {
                    core.show(moved);
                } else if (moved) {
                    moved.style.display = 'block';
                    moved.style.visibility = 'visible';
                    moved.style.opacity = '1';
                    moved.style.transform = 'translateY(0)';
                }
            });

            if (typeof this.options.onShow === 'function') {
                this.options.onShow.call(this.$element[0]);
            }

            this.$element.trigger('show.ui.avatar');
        }


        positionDropdown() {
            // 下拉菜单可能已被移动到 body，按 instance 查找更稳健
            const $dropdown = $(`[data-avatar-instance="${this.instanceId}"]`);
            if (!$dropdown.length) return;

            const $img = this.$element.find('.ui-avatar-img');
            if (!$img.length) return;

            const core = getDropdownCore();
            if (core) {
                core.place($dropdown[0], $img[0], {
                    placement: 'auto',
                    offset: 8,
                    viewportMargin: 16,
                    appendToBody: true,
                    visible: this.isOpen,
                    preferEndOnRight: true
                });
                return;
            }

            // 极简兜底：无 dropdown core 时，至少保证菜单跟随锚点可见
            const rect = $img[0].getBoundingClientRect();
            $dropdown.css({
                position: 'fixed',
                left: `${Math.round(rect.left)}px`,
                top: `${Math.round(rect.bottom + 8)}px`,
                display: 'block',
                visibility: 'visible',
                opacity: 1
            });
        }

        hide() {
            if (!this.isOpen) return;

            this.$element.removeClass('active');
            this.isOpen = false;

            if (typeof this.options.onHide === 'function') {
                this.options.onHide.call(this.$element[0]);
            }

            // 隐藏可能被移动到 body 的下拉菜单
            const $moved = $(`[data-avatar-instance="${this.instanceId}"]`);
            if ($moved.length) {
                const core = getDropdownCore();
                if (core) {
                    core.hide($moved[0]);
                } else {
                    $moved.css({ opacity: 0, visibility: 'hidden', transform: 'translateY(-10px)', display: 'none' });
                }
            }

            this.$element.trigger('hide.ui.avatar');
        }

        updateImage(src) {
            const normalizedSrc = normalizeAvatarSrc(src);
            const $img = this.$element.find('.ui-avatar-img img');
            const $default = this.$element.find('.ui-avatar-default');

            if (normalizedSrc) {
                if ($img.length) {
                    $img.attr('src', normalizedSrc).show();
                    $default.hide();
                } else {
                    this.$element.find('.ui-avatar-img').prepend(`<img src="${normalizedSrc}" alt="User">`);
                    $default.hide();
                }
            } else {
                $img.hide();
                $default.show();
            }

            this.options.src = normalizedSrc;
        }

        updateStatus(status) {
            const $status = this.$element.find('.ui-avatar-status');
            if ($status.length) {
                $status.removeClass('online offline busy away').addClass(status);
            } else if (this.options.showStatus) {
                this.$element.find('.ui-avatar-img').append(`<span class="ui-avatar-status ${status}"></span>`);
            }
            this.options.status = status;
        }

        updateNotification(hasNotification) {
            this.$element.toggleClass('has-notification', hasNotification);
            this.options.hasNotification = hasNotification;
        }

        destroy() {
            this.$element.off('.ui.avatar');
            // 移除 document/window 上与该实例相关的所有命名空间事件
            $(document).off('.' + this.instanceId);
            $(window).off('.' + this.instanceId);
            window.clearTimeout(this._resizeTimer);

            // 如果下拉菜单被移动到 body，上面会带有 data-avatar-instance 标识，确保移除它
            const $moved = $(`[data-avatar-instance="${this.instanceId}"]`);
            if ($moved.length) {
                $moved.remove();
            }

            this.$element.removeData('ui.avatar.instance');
            this.$element.empty().removeClass('ui-avatar active has-notification ui-avatar-sm ui-avatar-lg ui-avatar-xl');
        }
    }

    // jQuery 插件定义
    $.fn.avatar = function(options) {
        return this.each(function() {
            let instance = $(this).data('ui.avatar.instance');

            if (typeof options === 'string') {
                // 方法调用
                if (instance && typeof instance[options] === 'function') {
                    const args = Array.prototype.slice.call(arguments, 1);
                    instance[options].apply(instance, args);
                }
            } else {
                // 初始化
                if (instance) {
                    instance.destroy();
                }
                instance = new Avatar(this, options);
                $(this).data('ui.avatar.instance', instance);
            }
        });
    };

    // 数据 API
    $(document).ready(function() {
        $('[data-toggle="avatar"]').each(function() {
            const $this = $(this);
            const options = {
                src: $this.data('src'),
                defaultIcon: $this.data('icon') || '👤',
                userName: $this.data('name'),
                userEmail: $this.data('email'),
                status: $this.data('status'),
                showStatus: $this.data('show-status') === true || $this.data('show-status') === 'true',
                size: $this.data('size') || 'md',
                hasNotification: $this.data('notification') === true || $this.data('notification') === 'true'
            };

            if (isBoringAvatarElement($this) && !options.src) {
                // boring-avatar 模式交给 ui.avatar.boring.js，避免两套渲染逻辑互相覆盖。
                return;
            }

            $this.avatar(options);
        });
    });

})(jQuery);
